import store from 'store/store';
import PDFAnnotationManager from 'components/PDFViewer/PDFAnnotationManager';
import PDFTronManager from 'components/PDFViewer/PDFTronManager';
import { PDFManagerFactory } from 'pdftron';
import { resetCursors } from 'pdftron/docManager/AnnotationTools/utils/CropToolUtils';
import { AnnotationCustomData, DocumentTypes, MarqueeZoneCustomData, ZoneCustomData } from 'types';
import { getManualSelectedZoneId, getSelectedTool, inspection } from 'store';
import { useDocumentToastStateStore } from 'zstore/documentToastStateStore';
import { Core } from '@pdftron/webviewer';
import { useMarqueeZoneStateStore } from 'zstore/marqueeZoneStore';

export function clearInputAnnotations() {
  const state = store.getState();
  const selectedTool = getSelectedTool(state);

  store.dispatch(inspection.actions.resetSelectedTexts());

  const pdfDocManager = PDFManagerFactory.getPDFDocManager();
  pdfDocManager?.exitManualAlignMode();

  PDFAnnotationManager.deleteAllInputAnnotations();
  resetCursors();

  store.dispatch(inspection.actions.setTextZoneId(1));
  store.dispatch(inspection.actions.setMarqueeZoneId(1));
  store.dispatch(inspection.actions.setGraphicZoneId(1));
  store.dispatch(inspection.actions.setCrossoutZoneId(1));
  store.dispatch(inspection.actions.setCropZoneId(1));
  store.dispatch(inspection.actions.setManualAlignZoneId(1));
  store.dispatch(inspection.actions.setColorPointId(1));

  if (getManualSelectedZoneId(store.getState())) {
    store.dispatch(inspection.actions.deactivateManualSelection());
    PDFTronManager.setToolMode(selectedTool);

    PDFManagerFactory.getViewer(DocumentTypes.source)?.addDocumentHighlight();
    PDFManagerFactory.getViewer(DocumentTypes.target)?.removeAnnotationDocumentHighlight();
  }

  pdfDocManager?.deleteAllPolygonZones(DocumentTypes.source);
  pdfDocManager?.deleteAllPolygonZones(DocumentTypes.target);

  pdfDocManager?.updateInputAnnotations();

  useMarqueeZoneStateStore.getState().setPendingTargetMarqueeZoneNumber(0);

  if (selectedTool === 'GraphicCreateTool') {
    useDocumentToastStateStore.getState().expireAllToast();
  }
}

export function clearTextInputAnnotations() {
  // remove text zones, marquee zones and crossouts from annotation manager and redux
  store.dispatch(inspection.actions.resetSelectedTexts());

  Object.values(DocumentTypes).forEach((docType) => {
    const inputAnnotations = PDFAnnotationManager.getInputAnnotations(docType);
    const annotationManager = PDFAnnotationManager.getInstance(docType);
    if (!inputAnnotations || !annotationManager) {
      return;
    }

    // We need to get the actual annotation from PDFTron as our annotation type is different
    const annotationsToDelete: Core.Annotations.Annotation[] = inputAnnotations
      .filter((inputAnnot) => {
        const annotation = annotationManager.getAnnotationById(inputAnnot.annotationId);
        return (
          annotation.getCustomData(AnnotationCustomData.strikeoutAnnotation) === 'true' ||
          annotation.getCustomData(ZoneCustomData.zoneAnnotation) === 'true' ||
          annotation.getCustomData(MarqueeZoneCustomData.marqueeZoneAnotation) === 'true'
        );
      })
      .map((inputAnnotation) => annotationManager.getAnnotationById(inputAnnotation.annotationId));

    // delete annotations list
    annotationManager.deleteAnnotations(annotationsToDelete, {
      imported: true,
      force: true,
    });
  });

  resetCursors();

  store.dispatch(inspection.actions.setTextZoneId(1));
  store.dispatch(inspection.actions.setMarqueeZoneId(1));
  store.dispatch(inspection.actions.setCrossoutZoneId(1));

  const selectedTool = getSelectedTool(store.getState());

  if (getManualSelectedZoneId(store.getState())) {
    store.dispatch(inspection.actions.deactivateManualSelection());
    PDFTronManager.setToolMode(selectedTool);
    PDFManagerFactory.getViewer(DocumentTypes.source)?.addDocumentHighlight();
    PDFManagerFactory.getViewer(DocumentTypes.target)?.removeAnnotationDocumentHighlight();
  }

  // update redux with updated annotations
  PDFManagerFactory?.getPDFDocManager()?.updateInputAnnotations();

  if (selectedTool === 'GraphicCreateTool') {
    useDocumentToastStateStore.getState().expireAllToast();
  }
}
